<script setup lang="ts">
export type ProjectFilesSelectProjectOption = {
  id: number | string
  name?: string | null
  title?: string | null
}

interface Props {
  modelValue: boolean
  project: ProjectFilesSelectProjectOption | null
  projects: ProjectFilesSelectProjectOption[]
  title?: string
  label?: string
  confirmText?: string
  loading?: boolean
  optionsLoading?: boolean
  hasMoreOptions?: boolean
  searchable?: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Select Project',
  label: 'Project',
  confirmText: 'Save',
  loading: false,
  optionsLoading: false,
  hasMoreOptions: false,
  searchable: false,
  disabled: false,
})

interface Emit {
  (event: 'update:modelValue', value: boolean): void
  (event: 'update:project', value: ProjectFilesSelectProjectOption | null): void
  (event: 'confirm'): void
  (event: 'search', value: string): void
  (event: 'load-more'): void
}

const emit = defineEmits<Emit>()

const isOpen = computed({
  get: () => props.modelValue,
  set: value => {
    emit('update:modelValue', value)
  },
})

const selectedProject = computed({
  get: () => props.project,
  set: value => {
    if (!value && props.searchable && props.project) return

    emit('update:project', value)
  },
})

const projectTitle = (project: ProjectFilesSelectProjectOption) => {
  return project.name ?? project.title ?? `Project #${project.id}`
}

const orderedProjects = computed(() => {
  const projectsById = new Map<number | string, ProjectFilesSelectProjectOption>()

  if (selectedProject.value) {
    projectsById.set(selectedProject.value.id, selectedProject.value)
  }
  props.projects.forEach(project => projectsById.set(project.id, project))

  return [...projectsById.values()]
})

const isBusy = computed(() => props.loading || props.optionsLoading)

const closeDialog = () => {
  if (props.loading) return
  isOpen.value = false
}

const onProjectSearchInput = (event: Event) => {
  if (!props.searchable) return

  emit('search', (event.target as HTMLInputElement | null)?.value || '')
}
</script>

<template>
  <VDialog v-model="isOpen" max-width="480">
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between">
        <span>{{ title }}</span>
        <VBtn
          icon="mdi-close"
          variant="text"
          size="small"
          :disabled="loading"
          @click="closeDialog"
        />
      </VCardTitle>
      <VCardText>
        <VAutocomplete
          v-model="selectedProject"
          :items="orderedProjects"
          :item-title="projectTitle"
          item-value="id"
          :label="label"
          :loading="optionsLoading"
          :disabled="disabled || loading"
          :no-filter="searchable"
          return-object
          density="compact"
          @input="onProjectSearchInput"
        >
          <template #no-data>
            <div class="px-4 py-3 text-medium-emphasis">
              {{
                optionsLoading
                  ? 'Searching...'
                  : searchable
                    ? 'No Projects found.'
                    : 'No Projects available.'
              }}
            </div>
          </template>
          <template
            v-if="hasMoreOptions"
            #append-item
          >
            <VDivider />
            <VListItem
              class="cursor-pointer"
              :disabled="isBusy"
              @click="emit('load-more')"
            >
              <template #prepend>
                <VProgressCircular
                  v-if="optionsLoading"
                  indeterminate
                  size="18"
                  width="2"
                />
                <VIcon
                  v-else
                  icon="tabler-chevron-down"
                  size="18"
                />
              </template>
              <VListItemTitle>Load more...</VListItemTitle>
            </VListItem>
          </template>
        </VAutocomplete>
      </VCardText>
      <VCardActions>
        <VSpacer />
        <VBtn
          variant="text"
          :disabled="loading"
          @click="closeDialog"
        >
          Cancel
        </VBtn>
        <VBtn
          color="primary"
          :loading="loading"
          :disabled="disabled || !selectedProject"
          @click="emit('confirm')"
        >
          {{ confirmText }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
